import React, { useCallback, useMemo, useState } from 'react';
import { useUpdateEffect, useRouter } from 'common/hooks';
import { useDebounced } from 'common/hooks/use-debounced';
import { Suggestions, CategorySuggestion } from 'types/suggestions';
import { patchUrlByLocale } from 'utils';
import { useStoreon } from 'storeon/react';
import { ProductRedirectionUrl } from 'types/product-url';
import { Region } from 'types/model';
import { ProductRedirectionEvents } from 'actions';
import { useKeyboardNavigation } from 'common/hooks/use-keyboard-navigation';
import { handleSubmit } from './handle-submit';
import { normalizeInput, getSanitizedValue, sanitizeString } from './utils';
import { TransitionState } from '../../types';
import { useSearchHistoryContext } from '../use-search-history-context';
import { prepareSuggestions } from './prepare-suggestions';
import { useSponsoredProducts } from 'components/header/hooks';
import { getSearchParsed } from 'common/hooks/use-openlink-url/utils';
import { SponsoredProduct } from 'types/sponsored-products';
import { trackingHelper } from 'utils/tracking-helper';
import { handleLmCode } from './handle-lmcode';

type UseSearchbarLogicParams = {
  getSuggestions: (value: string) => void;
  resetServices: () => void;
  collapse: () => void;
  transitionState: TransitionState;
  suggestions: Suggestions | null;
  suggestionsLoading?: boolean;
  error?: boolean;
  productRedirectionUrl?: ProductRedirectionUrl['redirectionUrl'];
  productRedirectionUrlLoading?: boolean;
  currentRegionId?: Region['regionId'];
  resetSponsoredProducts: () => void;
  resetSponsoredBrands: () => void;
};

export const useSearchbarLogic = (params: UseSearchbarLogicParams) => {
  const {
    getSuggestions,
    resetServices,
    transitionState,
    collapse,
    productRedirectionUrl,
    productRedirectionUrlLoading,
    currentRegionId,
    suggestions,
    resetSponsoredProducts,
    resetSponsoredBrands,
    suggestionsLoading,
  } = params;

  const router = useRouter();
  const path = router.getPath();
  const { dispatch } = useStoreon();

  const search = getSearchParsed();
  const [isSrp, setIsSrp] = useState(path.startsWith(patchUrlByLocale({ url: '/search/' })));

  const [initialValue, setInitialValue] = useState((search.q as string) ?? '');
  const [value, setValue] = useState(initialValue);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { addHistoryFromSubmit, addHistoryItem } = useSearchHistoryContext();
  const sponsoredProducts = useSponsoredProducts();
  const sponsoredSuggestions: CategorySuggestion[] = sponsoredProducts.map(
    (sponsoredItem: SponsoredProduct) => ({
      categoryUrl: sponsoredItem.productPageName,
      categoryName: '',
      categoryLabel: '',
    }),
  );
  const { slicedSuggestions, flatSuggestions } = useMemo(() => {
    return prepareSuggestions(suggestions, value);
  }, [suggestions, value]);

  const combinedSuggestions = flatSuggestions && [...flatSuggestions, ...sponsoredSuggestions];

  const {
    index,
    reset,
    selectedItem: selectedSuggestion,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
  } = useKeyboardNavigation(combinedSuggestions, { canUnselectAllItems: true });

  const handleGetSuggestions = useCallback(
    (searchString: string) => {
      getSuggestions(searchString);
    },
    [getSuggestions],
  );

  const dGetSuggestions = useDebounced(handleGetSuggestions, { interval: 200 });

  const prettifiedValue = useMemo(() => sanitizeString(value), [value]);

  const isLmcode = useMemo(
    () => /^\d+$/.test(prettifiedValue) && prettifiedValue.length === 8,
    [prettifiedValue],
  );

  const sanitizedValue = getSanitizedValue(selectedSuggestion) ?? prettifiedValue;
  const normalizedSanitizedValue = useMemo(() => normalizeInput(sanitizedValue), [sanitizedValue]);

  const shouldHandleLmcodeSubmit = isLmcode && !productRedirectionUrlLoading && isSubmitted;
  const suggestionsMismatch =
    (suggestions && suggestions.query !== normalizedSanitizedValue) || suggestionsLoading;
  const shouldHandleSuggestionsSubmit =
    isSubmitted && !suggestionsLoading && suggestions && suggestions.query === normalizedSanitizedValue;

  useUpdateEffect(() => {
    setInitialValue((search.q as string) ?? '');
  }, [search]);

  useUpdateEffect(() => {
    setIsSrp(path.startsWith(patchUrlByLocale({ url: '/search/' })));
  }, [path]);

  useUpdateEffect(() => {
    if (shouldHandleLmcodeSubmit) {
      handleLmCode(
        productRedirectionUrl,
        dispatch,
        setIsSubmitted,
        collapse,
        addHistoryItem,
        value,
        sanitizedValue,
      );
    }
  }, [shouldHandleLmcodeSubmit, productRedirectionUrl]);

  useUpdateEffect(() => {
    if (shouldHandleSuggestionsSubmit) {
      handleSubmit(
        value,
        sanitizedValue,
        selectedSuggestion,
        addHistoryFromSubmit,
        addHistoryItem,
        suggestions,
      );
      setIsSubmitted(false);
      collapse();
    }
  }, [shouldHandleSuggestionsSubmit]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitted(true);
    trackingHelper.dispatchSearchEvent(value);
    if (isLmcode && !productRedirectionUrlLoading && !productRedirectionUrl) {
      dispatch(ProductRedirectionEvents.request, {
        lmcode: sanitizedValue,
        regionId: currentRegionId,
      });
      return;
    }
    if (suggestionsMismatch && !selectedSuggestion) {
      return;
    }
    handleSubmit(
      value,
      sanitizedValue,
      selectedSuggestion,
      addHistoryFromSubmit,
      addHistoryItem,
      suggestions,
    );
    collapse();
  };

  const updateValue = useCallback(
    (nextValue: string) => {
      setValue(nextValue);
      reset();
      if (nextValue === '') {
        resetServices();
        resetSponsoredProducts();
        resetSponsoredBrands();
      }
      dGetSuggestions(normalizeInput(nextValue));
    },
    [dGetSuggestions, reset, resetServices, resetSponsoredProducts, resetSponsoredBrands],
  );

  useUpdateEffect(() => {
    if (transitionState === 'collapsed') {
      setValue(initialValue);
      reset();
      if (initialValue === '') resetServices();
    } else if (transitionState === 'needsExpand') {
      updateValue(value);
    }
  }, [initialValue, updateValue, transitionState, reset]);

  return {
    isSrp,
    onSubmit,
    value,
    updateValue,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
    index,
    selectedSuggestion,
    slicedSuggestions,
  };
};
